import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import "./apiKeys.scss";
import {
    getClientCompanies,
    createClientCompany,
    updateClientCompanyStatus,
    updateClientCompany,
    getClientCompanyById,
} from '../../Redux/ActionCreators/settingAction';
import ToggleSwitch from '../../utils/toggleSwitch';
import { useAppPermission } from '../../hooks/useAppPermission';
import { P } from '../../constants/rbac';

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const renderCustomPagination = (totalPages, currentPage, setCurrentPage) => {
    const pagination = [];

    pagination.push(
        <button className="jv2-page-btn" key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
    );

    if (currentPage <= 3) {
        pagination.push(
            <button key={1} className={`jv2-page-btn${currentPage === 1 ? ' active' : ''}`} onClick={() => setCurrentPage(1)}>1</button>
        );
    } else {
        pagination.push(<span key="left-dots" className="jv2-page-ellipsis">…</span>);
    }

    const startNum = Math.max(2, currentPage - 1);
    const endNum = Math.min(totalPages - 1, currentPage + 1);

    for (let i = startNum; i <= endNum; i++) {
        pagination.push(
            <button key={i} className={`jv2-page-btn${currentPage === i ? ' active' : ''}`} onClick={() => setCurrentPage(i)}>{i}</button>
        );
    }

    if (currentPage >= totalPages - 2) {
        if (totalPages > 1) {
            pagination.push(
                <button key={totalPages} className={`jv2-page-btn${currentPage === totalPages ? ' active' : ''}`} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
            );
        }
    } else {
        pagination.push(<span key="right-dots" className="jv2-page-ellipsis">…</span>);
    }

    pagination.push(
        <button className="jv2-page-btn" key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
    );

    return pagination;
};

const ClientCompanies = () => {
    const dispatch = useDispatch();
    const { can } = useAppPermission();
    const canCreate = can(P.SETTINGS_CLIENT_COMPANY_CREATE);
    const canEdit = can(P.SETTINGS_CLIENT_COMPANY_EDIT);

    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [form, setForm] = useState({ name: '', code: '' });
    const [formErrors, setFormErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

    const SESSION_PAGE_SIZE_KEY = useMemo(() => `clientCompanies_pageSize`, []);
    const SESSION_CURRENT_PAGE_KEY = useMemo(() => `clientCompanies_currentPage`, []);

    const safeGet = (key, fallback) => {
        try {
            const value = Number(window?.sessionStorage?.getItem(key));
            return Number.isFinite(value) && value > 0 ? value : fallback;
        } catch {
            return fallback;
        }
    };

    const [currentPage, setCurrentPage] = useState(() => safeGet(SESSION_CURRENT_PAGE_KEY, 1));
    const [pageSize, setPageSize] = useState(() => safeGet(SESSION_PAGE_SIZE_KEY, 10));
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimerRef = useRef(null);

    const clientCompanies = useSelector(state => state.setting.clientCompanies || []);
    const clientCompaniesTotal = useSelector(state => state.setting.clientCompaniesTotal || 0);

    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
        }, 350);
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, [searchTerm]);

    useEffect(() => {
        fetchRows(pageSize, 1, debouncedSearch);
        setCurrentPage(1);
    }, [pageSize, debouncedSearch]);

    useEffect(() => {
        sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, pageSize);
    }, [pageSize, SESSION_PAGE_SIZE_KEY]);

    useEffect(() => {
        sessionStorage.setItem(SESSION_CURRENT_PAGE_KEY, currentPage);
    }, [currentPage, SESSION_CURRENT_PAGE_KEY]);

    const fetchRows = async (pageSize = 10, page = 1, search = debouncedSearch) => {
        setLoading(true);
        await dispatch(getClientCompanies(pageSize, page, { search }));
        setLoading(false);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchRows(pageSize, page, debouncedSearch);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleToggleStatus = async (row) => {
        const newStatus = !row.is_active;
        await dispatch(updateClientCompanyStatus(row.id, newStatus));
    };

    const handleCreateClick = () => {
        setEditingRow(null);
        setForm({ name: '', code: '' });
        setFormErrors({});
        setShowModal(true);
    };

    const handleEditClick = (row) => {
        setEditingRow(row);
        setForm({
            name: row.name || '',
            code: row.code || '',
        });
        setFormErrors({});
        setShowModal(true);
    };

    const validate = () => {
        const errors = {};
        if (!form.name.trim()) errors.name = 'Company name is required';
        if (!form.code.trim()) errors.code = 'Company code is required';
        return errors;
    };

    const handleSave = async () => {
        const errors = validate();
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }
        setSaving(true);
        const payload = {
            name: form.name.trim(),
            code: form.code.trim(),
            is_active: true,
        };

        if (editingRow) {
            const result = await dispatch(updateClientCompany(editingRow.id, { name: payload.name }));
            if (result?.type === 'UPDATE_CLIENT_COMPANY') {
                await dispatch(getClientCompanyById(editingRow.id));
                setShowModal(false);
                setSnack({ open: true, message: 'Company updated successfully!', severity: 'success' });
            } else {
                setSnack({ open: true, message: result?.payload?.detail || 'Failed to update company. Please try again.', severity: 'error' });
            }
        } else {
            const result = await dispatch(createClientCompany(payload));
            if (result?.type === 'CREATE_CLIENT_COMPANY') {
                setShowModal(false);
                setSnack({ open: true, message: 'Company created successfully!', severity: 'success' });
                fetchRows(pageSize, 1);
            } else {
                setSnack({ open: true, message: result?.payload?.detail || 'Failed to create company. Please try again.', severity: 'error' });
            }
        }
        setSaving(false);
    };

    const totalPages = Math.ceil(clientCompaniesTotal / pageSize) || 1;

    return (
        <div className="job-table-container">
            <div className="top-navigation">
                <div className='createjob-tab'>
                    <h4>Companies</h4>
                    {canCreate && (
                        <button className="create-job" onClick={handleCreateClick} style={{ width: 'unset' }}>
                            Create Company
                        </button>
                    )}
                </div>
            </div>

            <div className="table-content" style={{ background: '#FFFFFF' }}>
                <div className="settings-filter-bar" style={{ justifyContent: 'flex-start' }}>
                    <div className="has-search" style={{ flex: 1, maxWidth: 360 }}>
                        <span className="form-control-feedback">
                            <img src="/images/search.svg" alt="" />
                        </span>
                        <input
                            type="text"
                            className={`search-bar${searchTerm ? ' has-clear' : ''}`}
                            placeholder="Search companies by name or code"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="search-clear"
                                title="Clear search"
                                aria-label="Clear search"
                                onClick={() => setSearchTerm('')}
                            >
                                ×
                            </button>
                        )}
                    </div>
                </div>
                <div className="jv2-table-outer">
                    <div className="jv2-table-scroll">
                        <table className="jv2-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Code</th>
                                    <th>Status</th>
                                    <th>Created At</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="jv2-loading-cell">
                                            <div className="spinner-border" role="status" style={{ color: '#F75891', width: '28px', height: '28px' }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : clientCompanies.length === 0 ? (
                                    <tr><td colSpan="5" className="jv2-empty-cell">No companies found.</td></tr>
                                ) : (
                                    clientCompanies.map(row => (
                                        <tr key={row.id}>
                                            <td>{row.name}</td>
                                            <td>{row.code}</td>
                                            <td>
                                                {canEdit ? (
                                                    <ToggleSwitch
                                                        checked={!!row.is_active}
                                                        onChange={() => handleToggleStatus(row)}
                                                    />
                                                ) : (
                                                    <span>{row.is_active ? 'Active' : 'Inactive'}</span>
                                                )}
                                            </td>
                                            <td>{formatDate(row.created_at)}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                {canEdit && (
                                                    <img
                                                        className="cursor-pointer"
                                                        src="/images/edit-black-icon.svg"
                                                        alt="Edit"
                                                        width={16}
                                                        height={16}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleEditClick(row)}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {!loading && (
                        <div className="jv2-pagination">
                            <span className="jv2-pagination-info">
                                {clientCompaniesTotal ? (
                                    <>Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, clientCompaniesTotal)} of <strong>{clientCompaniesTotal}</strong> Companies</>
                                ) : 'Showing 0 of 0'}
                            </span>
                            <div className="jv2-pagination-right">
                                <div className="jv2-page-btns">
                                    {renderCustomPagination(totalPages, currentPage, handlePageChange)}
                                </div>
                                <div className="jv2-per-page">
                                    <span>Per page:</span>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="upload-modal automation-modal">
                    <div className="modal-content">
                        <div className="modal-head">
                            <h3>{editingRow ? 'Edit Company' : 'Create Company'}</h3>
                            <img
                                src="/images/close-icon.svg"
                                alt="close"
                                onClick={() => setShowModal(false)}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                        <div className="modal-input">
                            <div className="automation-dropdown">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Acme Corp"
                                    value={form.name}
                                    onChange={e => {
                                        setForm({ ...form, name: e.target.value });
                                        setFormErrors({ ...formErrors, name: '' });
                                    }}
                                />
                                {formErrors.name && (
                                    <span style={{ color: '#e53e3e', fontSize: '12px' }}>
                                        {formErrors.name}
                                    </span>
                                )}
                            </div>
                            <div className="automation-dropdown">
                                <label>Code *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. ACME"
                                    value={form.code}
                                    onChange={e => {
                                        setForm({ ...form, code: e.target.value });
                                        setFormErrors({ ...formErrors, code: '' });
                                    }}
                                    disabled={!!editingRow}
                                />
                                {formErrors.code && (
                                    <span style={{ color: '#e53e3e', fontSize: '12px' }}>
                                        {formErrors.code}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="d-flex justify-content-end" style={{ margin: '0 30px 20px 30px' }}>
                            <button
                                className="upload-btn"
                                onClick={() => setShowModal(false)}
                                style={{ backgroundColor: 'transparent', border: '1px solid #111928', color: '#111928' }}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                className="upload-btn"
                                onClick={handleSave}
                                style={{ backgroundColor: saving ? '#ccc' : '' }}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert severity={snack.severity}>{snack.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default ClientCompanies;
